import { useState } from "react";
import { FaTag, FaTimes, FaCheckCircle } from "react-icons/fa";
import { validatePromoCode, formatPrice } from "../../services/ticketingService";
import "./PromoCodeInput.css";

/**
 * Promo code input with validation.
 *
 * Props:
 *  - eventId: int
 *  - items: [{ tier_id, quantity }]  — current cart items
 *  - customerToken: optional loyalty token
 *  - appliedPromo: { code, discount_cents, description } | null
 *  - onApply(promo): called when a valid code is applied
 *  - onRemove(): called when the code is removed
 */
export default function PromoCodeInput({
  eventId,
  items,
  customerToken,
  appliedPromo,
  onApply,
  onRemove,
}) {
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const hasItems = items && items.some((i) => i.quantity > 0);

  const handleApply = async () => {
    const code = inputValue.trim().toUpperCase();
    if (!code) return;

    setLoading(true);
    setError("");
    try {
      const result = await validatePromoCode({
        code,
        eventId,
        items: items.filter((i) => i.quantity > 0),
        customerToken,
      });
      onApply(result);
      setInputValue("");
    } catch (err) {
      setError(err.message || "Invalid promo code");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleApply();
  };

  // Applied state
  if (appliedPromo) {
    return (
      <div className="promo-applied">
        <FaCheckCircle className="promo-applied-icon" />
        <div className="promo-applied-info">
          <span className="promo-applied-code">{appliedPromo.code}</span>
          {appliedPromo.description && (
            <span className="promo-applied-desc">{appliedPromo.description}</span>
          )}
        </div>
        <span className="promo-applied-amount">
          &minus;{formatPrice(appliedPromo.discount_cents)}
        </span>
        <button
          className="promo-remove-btn"
          onClick={onRemove}
          aria-label="Remove promo code"
          type="button"
        >
          <FaTimes />
        </button>
      </div>
    );
  }

  return (
    <div className="promo-input-wrap">
      <div className="promo-input-row">
        <FaTag className="promo-input-icon" />
        <input
          className={`promo-input${error ? " promo-input--error" : ""}`}
          type="text"
          placeholder="Promo code"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value.toUpperCase());
            if (error) setError("");
          }}
          onKeyDown={handleKeyDown}
          disabled={loading || !hasItems}
          autoComplete="off"
          spellCheck={false}
        />
        <button
          className="promo-apply-btn"
          onClick={handleApply}
          disabled={loading || !inputValue.trim() || !hasItems}
          type="button"
        >
          {loading ? "…" : "Apply"}
        </button>
      </div>
      {error && <p className="promo-error">{error}</p>}
      {!hasItems && (
        <p className="promo-hint">Select tickets above to apply a promo code</p>
      )}
    </div>
  );
}
